import React, { useState, useEffect } from 'react';
import AdminPageLayout from '../components/admin/AdminPageLayout';
import {
  Box,
  Container,
  Typography,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  Stack,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  Grid
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  LocationOn as LocationIcon,
  AttachMoney as MoneyIcon
} from '@mui/icons-material';
import axios from 'axios';
import { fetchJobsNearMaid, getJobStatusColor } from '../services/jobService';
import logger from '../utils/logger';

const AdminJobsNearMaid = () => {
  // State management
  const [maids, setMaids] = useState([]);
  const [selectedMaid, setSelectedMaid] = useState('');
  const [radiusMiles, setRadiusMiles] = useState(25);
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [maidsLoading, setMaidsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [jobsData, setJobsData] = useState(null);

  // Fetch maids on component mount
  useEffect(() => {
    fetchMaids();
  }, []);

  const fetchMaids = async () => {
    try {
      setMaidsLoading(true);
      setError(null);

      const token = localStorage.getItem('token');
      const apiBase = process.env.REACT_APP_API_URL?.replace(/\/$/, '') || '';

      const response = await axios.get(`${apiBase}/api/maids/light`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          activeOnly: true,
          limit: 500
        }
      });

      setMaids(response.data.data || []);
    } catch (err) {
      logger.log('Error fetching maids:', err);
      setError('Failed to load technicians. Please try again.');
    } finally {
      setMaidsLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!selectedMaid) {
      setError('Please select a technician');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const options = {
        radiusMiles: parseFloat(radiusMiles),
        status: statusFilter || undefined
      };

      const result = await fetchJobsNearMaid(selectedMaid, options);
      setJobsData(result.data);
    } catch (err) {
      logger.log('Error fetching jobs near maid:', err);
      setError(err.message || 'Failed to fetch jobs. Please try again.');
      setJobsData(null);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setSelectedMaid('');
    setRadiusMiles(25);
    setStatusFilter('');
    setJobsData(null);
    setError(null);
  };

  const formatAddress = (address) => {
    if (typeof address === 'string') return address;
    if (typeof address === 'object' && address !== null) {
      const { street, city, state, zip } = address;
      return [street, city, state, zip].filter(Boolean).join(', ');
    }
    return 'N/A';
  };

  const formatCurrency = (cents) => {
    if (!cents || cents === 0) return '$0.00';
    return `$${(cents / 100).toFixed(2)}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatTime = (timeString) => {
    if (!timeString) return '';
    const date = new Date(timeString);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  return (
    <AdminPageLayout title="Jobs Near Technician">

      {/* Search Controls */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <FormControl fullWidth disabled={maidsLoading}>
              <InputLabel>Select Technician</InputLabel>
              <Select
                value={selectedMaid}
                onChange={(e) => setSelectedMaid(e.target.value)}
                label="Select Technician"
              >
                <MenuItem value="">
                  <em>Select a technician</em>
                </MenuItem>
                {maids.map((maid) => (
                  <MenuItem key={maid.id} value={maid.id}>
                    {maid.firstName} {maid.lastName} - {maid.email}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              type="number"
              label="Radius (miles)"
              value={radiusMiles}
              onChange={(e) => setRadiusMiles(e.target.value)}
              inputProps={{ min: 1, max: 100, step: 5 }}
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Status Filter</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                label="Status Filter"
              >
                <MenuItem value="">All Statuses</MenuItem>
                <MenuItem value="NOT_STARTED">Not Started</MenuItem>
                <MenuItem value="IN_PROGRESS">In Progress</MenuItem>
                <MenuItem value="COMPLETED">Completed</MenuItem>
                <MenuItem value="CANCELLED">Cancelled</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={3}>
            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                startIcon={loading ? <CircularProgress size={20} /> : <SearchIcon />}
                onClick={handleSearch}
                disabled={loading || !selectedMaid}
                fullWidth
              >
                Search
              </Button>
              <Tooltip title="Reset">
                <IconButton onClick={handleReset} disabled={loading}>
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Results Summary */}
      {jobsData && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Technician Information
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Name:</strong> {jobsData.maid.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Email:</strong> {jobsData.maid.email}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Address:</strong> {formatAddress(jobsData.maid.address)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Search Results
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Search Radius:</strong> {jobsData.searchRadius} miles
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Jobs Found:</strong> {jobsData.jobsFound}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Total Value:</strong>{' '}
                  {formatCurrency(
                    jobsData.jobs.reduce((sum, job) => sum + (job.totalAmount || 0), 0)
                  )}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Jobs Table */}
      {jobsData && jobsData.jobs.length > 0 && (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>Job #</strong></TableCell>
                <TableCell><strong>Customer</strong></TableCell>
                <TableCell><strong>Address</strong></TableCell>
                <TableCell><strong>Distance</strong></TableCell>
                <TableCell><strong>Scheduled</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
                <TableCell><strong>Value</strong></TableCell>
                <TableCell><strong>Assigned Technicians</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {jobsData.jobs.map((job) => (
                <TableRow key={job.id} hover>
                  <TableCell>{job.jobNumber || 'N/A'}</TableCell>
                  <TableCell>
                    {job.customer ? (
                      <Box>
                        <Typography variant="body2">
                          {job.customer.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {job.customer.email}
                        </Typography>
                      </Box>
                    ) : (
                      'N/A'
                    )}
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <LocationIcon fontSize="small" color="action" />
                      <Typography variant="body2">
                        {formatAddress(job.address)}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={`${job.distanceMiles} mi`}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2">
                        {formatDate(job.scheduledDate)}
                      </Typography>
                      {job.scheduledTime && (
                        <Typography variant="caption" color="text.secondary">
                          {formatTime(job.scheduledTime)}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={job.status || 'Unknown'}
                      size="small"
                      sx={{
                        backgroundColor: getJobStatusColor(job.status),
                        color: 'white'
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <MoneyIcon fontSize="small" color="success" />
                      <Typography variant="body2">
                        {formatCurrency(job.totalAmount)}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    {job.assignedMaids && job.assignedMaids.length > 0 ? (
                      <Stack direction="column" spacing={0.5}>
                        {job.assignedMaids.map((maid, idx) => (
                          <Chip
                            key={idx}
                            label={maid.name || 'Unknown'}
                            size="small"
                            variant="outlined"
                          />
                        ))}
                      </Stack>
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        Unassigned
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* No Results */}
      {jobsData && jobsData.jobs.length === 0 && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            No jobs found within {jobsData.searchRadius} miles
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Try increasing the search radius or changing the filters
          </Typography>
        </Paper>
      )}

      {/* Loading State */}
      {maidsLoading && (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      )}
    </AdminPageLayout>
  );
};

export default AdminJobsNearMaid;